import PropTypes from 'prop-types'
import React from 'react'
import {
  Dialog,
  DialogTitle,
  Button,
  DialogActions,
  useMediaQuery,
  useTheme,
  DialogContent,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import InfoPopover from '../info-popover'

import { ReactComponent as CloseIcon } from '../../assets/ic_close.svg'

const ActionsContainerCenter = {
  marginTop: 2,
  justifyContent: 'center',
}

const ActionsContainerEnd = {
  marginTop: 2,
}

const TitleContainer = {
  borderBottom: '2px solid #e3e3ec',
  paddingLeft: 1,
}

BaseDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  infoMessage: PropTypes.string,
  children: PropTypes.any,
  hideCloseButton: PropTypes.bool,
  loading: PropTypes.bool,
  openButtonTitle: PropTypes.string,
  closeButtonTitle: PropTypes.string,
  hideActions: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  classes: PropTypes.any,
  width: PropTypes.string,
  titleAction: PropTypes.node,
  primaryColor: PropTypes.string,
}

export default function BaseDialog({
  open,
  onClose,
  onConfirm,
  title,
  infoMessage,
  children,
  hideCloseButton,
  loading,
  openButtonTitle,
  closeButtonTitle,
  hideActions,
  confirmDisabled = false,
  classes: overrideClasses,
  width = 'sm',
  titleAction = null,
  primaryColor = null,
}) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const mainColor = primaryColor || theme.palette.primary.main
  const { t } = useTranslation()
  const openButtonTitleTranslation = openButtonTitle || t('common.confirm')
  const closeButtonTitleTranslation = closeButtonTitle || t('common.cancel')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      classes={overrideClasses}
      maxWidth={width}
      fullWidth={!!width}
    >
      {!hideCloseButton && (
        <DialogTitle style={TitleContainer}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Grid container alignItems="center">
                <Grid item marginLeft={1}>
                  <IconButton
                    onClick={onClose}
                    sx={{ color: mainColor, '& path': { fill: mainColor } }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Grid>

                <Grid item marginLeft={2}>
                  <Typography variant="h6">{title}</Typography>
                </Grid>
                {infoMessage && (
                  <Grid item>
                    <InfoPopover message={infoMessage} />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {titleAction && (
              <Grid item marginRight={1}>
                {titleAction}
              </Grid>
            )}
          </Grid>
        </DialogTitle>
      )}

      <DialogContent classes={overrideClasses} sx={{ pt: 4 }}>
        {loading ? (
          <Grid
            container
            justify="space-around"
            justifyContent="center"
            marginTop={2}
          >
            <Grid item>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : (
          <Box>{children}</Box>
        )}
      </DialogContent>
      {!hideActions && !loading && (
        <DialogActions
          className={
            hideCloseButton ? ActionsContainerCenter : ActionsContainerEnd
          }
          sx={{ p: 2 }}
        >
          <Button onClick={onClose} color="primary" variant="outlined">
            {closeButtonTitleTranslation}
          </Button>
          <Button
            onClick={onConfirm}
            color="primary"
            variant="contained"
            disabled={confirmDisabled}
          >
            {openButtonTitleTranslation}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
